/**
 * Media layer contracts.
 *
 * The editor never touches storage directly. Three independent, swappable concerns:
 *
 *  1. {@link MediaStorageProvider} — where media bytes live and how the app gets access to them
 *     (today: a local folder via the File System Access API).
 *  2. {@link MediaAnalyzer} + {@link DerivativeGenerator} — how files are inspected and how preview
 *     artefacts (thumbnail, filmstrip, waveform, proxy) are produced (today: in the browser with
 *     WebCodecs).
 *  3. {@link DerivativeCache} — where those artefacts are kept (today: the Origin Private File
 *     System).
 *
 * Each media asset in the project document records its provider id in `asset.location.provider`,
 * so projects can mix providers and new providers can be added without a schema change.
 */
import type { FilmstripInfo, MediaAsset, MediaKind, MediaLocation, Waveform } from '@ve/core';
import type { MediaInput } from '@ve/engine';

// ---------------------------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------------------------

/** Access state of a provider for one project on this device. */
export type ProviderAccess =
  /** Media can be read (and written, if the provider imports). `label` names the location. */
  | { state: 'ready'; label: string }
  /** The location is known but the user must re-grant access (needs a user gesture). */
  | { state: 'needs-permission'; label: string }
  /** Nothing configured on this device yet (e.g. no media folder chosen for this project). */
  | { state: 'not-configured' }
  /** This provider cannot work in this browser. */
  | { state: 'unsupported'; reason: string };

/** Something to bring into the project. */
export type ImportSource =
  /** A file the user picked or dropped (File System Access handle: no copy needed if possible). */
  | {
      kind: 'handle';
      handle: FileSystemFileHandle;
      /**
       * Library folders to file the media under, outermost first: the dropped directory and the
       * sub-directories leading to the file. Absent for loose files.
       */
      libraryPath?: string[];
    }
  /** Bytes produced by the app or obtained elsewhere (e.g. a generated or downloaded file). */
  | {
      kind: 'blob';
      blob: Blob;
      name: string;
      /** Optional sub-folder hint, e.g. "Generated". */ folder?: string;
    };

export interface StoredFile {
  location: MediaLocation;
  /** Readable snapshot of the stored file. */
  file: File;
  /** Display name. */
  name: string;
}

export interface ImportOptions {
  onProgress?(fraction: number): void;
  signal?: AbortSignal;
}

/** Where media bytes live and how the app gains access to them. */
export interface MediaStorageProvider {
  /** Stable id stored in `MediaLocation.provider`. */
  readonly id: string;
  /** Human-readable name, e.g. "Local folder". */
  readonly label: string;
  /** Whether new media can be imported through this provider (read-only providers return false). */
  readonly canImport: boolean;

  /** Feature detection for the current browser. */
  isSupported(): boolean;

  /** Current access state for a project. Never prompts. */
  getAccess(projectId: string): Promise<ProviderAccess>;

  /**
   * Obtains access, prompting the user if needed. MUST be called from a user gesture.
   * With `relocate`, lets the user pick a different location (e.g. after moving the folder).
   */
  requestAccess(projectId: string, options?: { relocate?: boolean }): Promise<ProviderAccess>;

  /** Stores an incoming file (copying or referencing it) and returns its location. */
  importFile(projectId: string, source: ImportSource, options?: ImportOptions): Promise<StoredFile>;

  /**
   * Opens a stored media file for reading. Throws {@link MediaAccessError} if it cannot be read
   * (no permission, missing, changed on disk).
   */
  open(projectId: string, location: MediaLocation): Promise<MediaInput>;

  /**
   * Lets the user choose files to import (e.g. a file picker). MUST be called from a user
   * gesture. Resolves to an empty list if the user cancels. Optional for read-only providers.
   */
  pickFiles?(projectId: string): Promise<ImportSource[]>;

  /**
   * Extracts importable sources from a drop event. MUST be called synchronously inside the
   * `drop` handler (browsers invalidate the data afterwards); the returned promise may resolve
   * later. Folders may be expanded to the media files inside them.
   */
  sourcesFromDrop?(dataTransfer: DataTransfer): Promise<ImportSource[]>;
}

export type MediaAccessReason = 'no-access' | 'missing' | 'changed' | 'unsupported';

export class MediaAccessError extends Error {
  constructor(
    message: string,
    readonly reason: MediaAccessReason,
  ) {
    super(message);
    this.name = 'MediaAccessError';
  }
}

// ---------------------------------------------------------------------------------------------
// Analysis & derivatives
// ---------------------------------------------------------------------------------------------

/** Facts learned by inspecting a file (becomes part of the `MediaAsset`). */
export interface MediaProbe {
  kind: MediaKind;
  duration?: number;
  width?: number;
  height?: number;
  fps?: number;
  hasAudio: boolean;
  hasVideo: boolean;
  /** When the media was recorded (ISO 8601), if the file's metadata says. */
  capturedAt?: string;
  /** User-presentable notes about a partially usable file (e.g. undecodable audio dropped). */
  warnings?: string[];
}

/** Thrown when a file is not media the editor can use; `message` is user-presentable. */
export class UnsupportedMediaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnsupportedMediaError';
  }
}

export interface MediaAnalyzer {
  /** Inspects a file. Throws {@link UnsupportedMediaError} for unusable files. */
  probe(input: MediaInput, fileName: string): Promise<MediaProbe>;
}

export type DerivativeKind = 'thumbnail' | 'filmstrip' | 'waveform' | 'proxy';

export interface DerivativeResult {
  blob: Blob;
  /** Filmstrip layout (only for `filmstrip`). */
  filmstrip?: FilmstripInfo;
}

export interface DerivativeContext {
  signal: AbortSignal;
  onProgress(fraction: number): void;
}

/** Produces preview artefacts from a media file. */
export interface DerivativeGenerator {
  /** Derivatives worth producing for this asset, in priority order. */
  plan(asset: MediaAsset): DerivativeKind[];
  generate(
    kind: DerivativeKind,
    asset: MediaAsset,
    input: MediaInput,
    ctx: DerivativeContext,
  ): Promise<DerivativeResult>;
}

/** Identifies one cached derivative. `fingerprint` changes when the source file changes. */
export interface DerivativeKey {
  mediaId: string;
  kind: DerivativeKind;
  fingerprint: string;
}

export interface CachedDerivative {
  file: File;
  filmstrip?: FilmstripInfo;
}

/** Where derivatives are kept. Contents are disposable: they can always be regenerated. */
export interface DerivativeCache {
  get(key: DerivativeKey): Promise<CachedDerivative | null>;
  put(key: DerivativeKey, result: DerivativeResult): Promise<CachedDerivative>;
  /** Removes every derivative of a media item. */
  remove(mediaId: string): Promise<void>;
  /** Total bytes used, if known. */
  usage?(): Promise<number>;
}

// ---------------------------------------------------------------------------------------------
// Runtime state (what the UI and engine consume)
// ---------------------------------------------------------------------------------------------

export type DerivativeState = 'pending' | 'processing' | 'ready' | 'failed' | 'skipped';

export type MediaAvailability =
  /** Being opened / checked. */
  | 'loading'
  /** Readable. */
  | 'online'
  /** Not readable right now (no permission, folder not connected, file missing). */
  | 'offline'
  /** Readable but unusable (e.g. changed on disk to something else). */
  | 'error';

export interface MediaRuntime {
  availability: MediaAvailability;
  /** User-presentable reason when not online. */
  message?: string;
  sources?: { master: MediaInput; proxy?: MediaInput };
  derivatives: Partial<Record<DerivativeKind, DerivativeState>>;
  /** Progress (0..1) of the derivative currently being generated. */
  progress?: number;
  thumbnailUrl?: string;
  filmstrip?: { url: string; info: FilmstripInfo };
  waveform?: Waveform;
}
